<template>
    <CompassPanel v-model="showPanel" :title="translations.title" @closed="onClose">
        <div v-if="!hasFailed" class="payment-status-panel__alert">
            <span class="typographies-paragraph-medium-black payment-status-panel__details">
                {{ props.chargeDate }} - {{ props.amount }}
            </span>
            <span class="payment-status-panel__tag">
                <CompassTag
                    :type="CompassEnums.TagType.COLORED"
                    :color="getTagColor()"
                    :size="CompassEnums.TagSize.SMALL"
                >
                    {{ props.directdebitStatus }}
                </CompassTag>
            </span>
        </div>

        <CompassAlert
            v-else
            :description="translations.apiError"
            :type="CompassEnums.AlertType.ERROR"
            :showIcon="true"
            :showClose="false"
        />

        <Timeline :isLoading="isLoading" :items="timelineItems" />

        <template #footer>
            <div class="payment-status-panel__faq">
                <CompassLink
                    :label="translations.faq"
                    link="https://help.sellsy.com/fr/articles/14960846-comprendre-les-etapes-d-un-prelevement-gocardless"
                    target="_blank"
                    type="secondary"
                    prefixIcon="external-link"
                />
            </div>
        </template>
    </CompassPanel>
</template>

<script setup lang="ts">
import { inject, onMounted, ref, watch } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import Timeline from 'vcomponents/common/timeline/Timeline.vue';
import { TimelineItem } from 'vcomponents/common/timeline/Timeline.type';
import { getPaymentTimeline } from 'api/gocardless/gocardlessService';
import { GocardlessTimelineItemResponse } from 'types/entities/payment/gocardlessDirectdebitTimeline.type';

interface Props {
    directdebitId: string;
    chargeDate: string;
    amount: string;
    directdebitStatus: string;
    paymentState: string;
    nextChargeDate: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    close: [];
}>();

const $t = inject('$t') as Translator;

const showPanel = ref(false);
const isLoading = ref(true);
const hasFailed = ref(false);
const timelineItems = ref([]);
const translations = ref({
    title: $t.__('document.PaymentStatusPanel.title'),
    fail: $t.__('common.label.fail'),
    apiError: $t.__('document.PaymentStatusPanel.api_error'),
    faq: $t.__('document.PaymentStatusPanel.faq'),
});

function createTimelineItem(item: GocardlessTimelineItemResponse): TimelineItem {
    return {
        icon: getIcon(item.status),
        title: $t.__(`document.PaymentStatusPanel.status.${item.status}.title`),
        label: moment(item.date).format('lll'),
        highlightedDate: getNextChargeDate(item.status),
    };
}

function getIcon(status: string): string {
    const mappingIconStatuses: Record<string, string> = {
        created: 'plus',
        submitted: 'send',
        customer_approval_granted: 'success-circle',
        customer_approval_denied: 'alert-circle',
        confirmed: 'success-circle',
        cancelled: 'alert-circle',
        failed: 'alert-circle',
        charged_back: 'reply',
        paid_out: 'success-circle',
        chargeback_cancelled: 'success-circle',
        late_failure_settled: 'alert-circle',
        chargeback_settled: 'reply',
        resubmission_requested: 'reload',
        surcharge_fee_debited: 'info-circle',
        sds_settlement_delayed: 'warning',
        resubmission_requested_auto: 'gocardless',
    };

    return mappingIconStatuses[status];
}

function getNextChargeDate(status: string): string | null {
    if (status === 'resubmission_requested_auto' && props.nextChargeDate !== '') {
        return props.nextChargeDate;
    }

    return null;
}

function getTagColor(): string {
    switch (props.paymentState) {
        case 'success':
            return CompassEnums.TagColorType.GREEN;
        case 'error':
            return CompassEnums.TagColorType.RED;
        case 'warning':
            return CompassEnums.TagColorType.YELLOW;
        case 'auto_pending':
            return CompassEnums.TagColorType.PURPLE;
        default:
            return CompassEnums.TagColorType.BLUE;
    }
}

function onClose(): void {
    showPanel.value = false;
    emit('close');
}

async function loadData(): Promise<void> {
    try {
        const response = await getPaymentTimeline(props.directdebitId);
        const items = response.data?.data ?? [];
        items.forEach((paymentItem: GocardlessTimelineItemResponse) => {
            timelineItems.value.push(createTimelineItem(paymentItem));
        });
    } catch (error) {
        hasFailed.value = true;
    } finally {
        isLoading.value = false;
    }
}

onMounted(async (): Promise<void> => {
    showPanel.value = true;
    await loadData();
});

watch(
    () => props.directdebitId,
    async () => {
        isLoading.value = true;
        hasFailed.value = false;
        timelineItems.value = [];
        await loadData();
    },
);
</script>

<style lang="scss" scoped>
.payment-status-panel {
    &__alert {
        display: flex;
        align-items: center;
        padding: var(--spacing-3);
        background: var(--colors-common-background-subtle);
        border-radius: 4px;
    }

    &__details {
        margin-left: var(--spacing-0);
    }

    &__tag {
        margin-left: var(--spacing-1);
    }

    &__faq {
        display: flex;
        justify-content: flex-end;
        margin: var(--spacing-3) var(--spacing-4);
    }
}
</style>
